import React, { useState, useEffect } from 'react';
import { BarChart3, Search, Zap, FileText, Download, RefreshCw, AlertCircle, CheckCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../utils/auth'; // Use your auth context
import api from '../utils/api'; // Use your API utility

const UsageDashboard = ({ onBack }) => {
  const { user, isAuthenticated } = useAuth(); // Use your auth context
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Mock tier limits - replace with actual limits from your backend
  const tierLimits = {
    free: { searches: 10, api_calls: 50, analyses: 5, exports: 2 },
    professional: { searches: 100, api_calls: 500, analyses: 50, exports: 20 },
    business: { searches: 500, api_calls: 2000, analyses: 200, exports: 100 },
    enterprise: { searches: -1, api_calls: -1, analyses: -1, exports: -1 } // unlimited
  };

  // FIXED: Complete fetchUsage function with proper error handling and response structure
  const fetchUsage = async () => {
    if (!isAuthenticated()) {
      setError('Please log in to view usage data');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // FIXED: Use the API client method instead of direct endpoint call
      // This ensures proper endpoint routing and authentication
      const usageData = await api.getUserUsage();
      console.log('✅ Usage data received:', usageData);
      
      // FIXED: API client returns data directly, not wrapped in response.data
      // Handle different possible response structures
      let finalUsageData = usageData;
      
      // If the backend returns data in a wrapper, unwrap it
      if (usageData && typeof usageData === 'object' && usageData.data) {
        finalUsageData = usageData.data;
      }
      
      // If still no usage data, create default structure
      if (!finalUsageData || typeof finalUsageData !== 'object') {
        console.log('⚠️ No usage data found, creating default structure');
        finalUsageData = {
          searches: 0,
          api_calls: 0,
          analyses: 0,
          exports: 0,
          tier: user?.tier || 'free',
          user_id: user?.id || 'unknown',
          last_updated: new Date().toISOString()
        };
      }
      
      setUsage(finalUsageData);
      
    } catch (err) {
      console.error('❌ Usage fetch error:', err);
      
      if (err.message.includes('Authentication') || err.message.includes('401')) {
        setError('Authentication expired. Please log in again.');
      } else {
        setError(err.message || 'Failed to fetch usage data');
      }
    } finally {
      // FIXED: Always set loading to false
      setLoading(false);
    }
  };

  // FIXED: Complete updateUsage function
  const updateUsage = async (type, increment = 1) => {
    if (!isAuthenticated()) {
      alert('Please log in to update usage.');
      return;
    }

    try {
      // FIXED: Use the API client method instead of direct call
      await api.updateUserUsage({ [type]: increment });
      
      // Refresh usage data
      await fetchUsage();
      
      // Show success feedback
      const button = event.target;
      const originalText = button.textContent;
      const originalClass = button.className;
      
      button.textContent = '✓ Updated';
      button.className = button.className.replace('bg-slate-700', 'bg-green-600');
      
      setTimeout(() => {
        button.textContent = originalText;
        button.className = originalClass;
      }, 1500);
      
    } catch (err) {
      console.error('Usage update error:', err);
      alert(`Update failed: ${err.message}`);
    }
  };

  // Test authentication function (for debugging)
  const testAuth = async () => {
    try {
      console.log('Testing auth with user:', user);
      
      // Test with your API utility
      const response = await api.getCurrentUser();
      console.log('✅ Auth test successful:', response);
      alert('✅ Authentication working! Check console for details.');
      
    } catch (err) {
      console.error('❌ Auth test failed:', err);
      alert(`❌ Auth test failed: ${err.message}`);
    }
  };

  useEffect(() => {
    fetchUsage();
  }, []);

  const getProgressPercentage = (current, limit) => {
    if (limit === -1) return 0; // unlimited
    return Math.min((current / limit) * 100, 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 70) return 'bg-yellow-500';
    return 'bg-blue-500';
  };

  const UsageCard = ({ icon: Icon, title, current, limit, type, color }) => {
    const percentage = getProgressPercentage(current, limit);
    const progressColor = getProgressColor(percentage);

    return (
      <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${color}`}>
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-white">{title}</h3>
              <p className="text-sm text-slate-400">
                {current} / {limit === -1 ? '∞' : limit.toLocaleString()}
              </p>
            </div>
          </div>
          <button
            onClick={(e) => updateUsage(type, 1)}
            className="px-3 py-1 bg-slate-700 hover:bg-slate-600 rounded-md text-sm font-medium text-white transition-colors"
          >
            Test +1
          </button>
        </div>
        
        {limit !== -1 && (
          <div className="w-full bg-slate-700 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all duration-300 ${progressColor}`}
              style={{ width: `${percentage}%` }}
            />
          </div>
        )}
        
        {limit !== -1 && percentage >= 90 && (
          <p className="text-xs text-red-400 mt-2 font-medium flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Approaching limit
          </p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 pt-24">
        <div className="container mx-auto px-4 py-8">
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-lg p-8">
            <div className="flex items-center justify-center space-x-2">
              <RefreshCw className="w-5 h-5 animate-spin text-blue-400" />
              <span className="text-slate-300">Loading usage data...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 pt-24">
        <div className="container mx-auto px-4 py-8">
          {/* Header with Back Button */}
          <div className="mb-8">
            {onBack && (
              <button
                onClick={onBack}
                className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </button>
            )}
            <h1 className="text-4xl font-bold text-white">Usage Dashboard</h1>
            <p className="text-slate-300">Detailed usage analytics and limits</p>
          </div>

          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-lg p-8">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
              <div className="text-red-400 mb-2 font-semibold">Error loading usage data</div>
              <p className="text-slate-300 mb-4">{error}</p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={fetchUsage}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                >
                  Retry
                </button>
                <button
                  onClick={testAuth}
                  className="px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-md transition-colors"
                >
                  Test Auth
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                If authentication keeps failing, try logging out and back in.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!usage) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 pt-24">
        <div className="container mx-auto px-4 py-8">
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-lg p-8">
            <div className="text-center text-slate-300">
              No usage data found. Start using the platform to see your stats!
              <br />
              <button
                onClick={testAuth}
                className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors text-sm"
              >
                Test Authentication
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const userTier = usage.tier || user?.plan || 'free';
  const limits = tierLimits[userTier] || tierLimits.free;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 pt-24">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              {onBack && (
                <button
                  onClick={onBack}
                  className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors mb-4"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Dashboard
                </button>
              )}
              <h1 className="text-4xl font-bold text-white mb-2">Usage Dashboard</h1>
              <p className="text-slate-300">
                Track your platform usage • Current Plan: <span className="font-semibold capitalize text-blue-400">{userTier}</span>
              </p>
              {user?.email && (
                <p className="text-sm text-slate-500">User: {user.email}</p>
              )}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={testAuth}
                className="flex items-center space-x-2 px-3 py-2 bg-green-600/20 hover:bg-green-600/30 text-green-300 border border-green-600/30 rounded-md transition-colors text-sm"
              >
                <CheckCircle className="w-4 h-4" />
                <span>Test Auth</span>
              </button>
              <button
                onClick={fetchUsage}
                className="flex items-center space-x-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-md transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>

        {/* Usage Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <UsageCard
            icon={Search}
            title="Searches"
            current={usage.searches || 0}
            limit={limits.searches}
            type="searches"
            color="bg-blue-600"
          />
          <UsageCard
            icon={Zap}
            title="API Calls"
            current={usage.api_calls || 0}
            limit={limits.api_calls}
            type="api_calls"
            color="bg-green-600"
          />
          <UsageCard
            icon={FileText}
            title="Analyses"
            current={usage.analyses || 0}
            limit={limits.analyses}
            type="analyses"
            color="bg-purple-600"
          />
          <UsageCard
            icon={Download}
            title="Exports"
            current={usage.exports || 0}
            limit={limits.exports}
            type="exports"
            color="bg-orange-600"
          />
        </div>

        {/* Details */}
        <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-lg p-6">
          <div className="flex items-center space-x-2 mb-4">
            <BarChart3 className="w-5 h-5 text-slate-400" />
            <h2 className="text-xl font-semibold text-white">Usage Details</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-white mb-3">Current Period</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Last Updated:</span>
                  <span className="font-medium text-slate-200">
                    {usage.last_updated ? new Date(usage.last_updated).toLocaleString() : 'Never'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">User ID:</span>
                  <span className="font-medium text-slate-200">{usage.user_id || user?.id || 'Unknown'}</span>
                </div>
                {usage.reset_date && (
                  <div className="flex justify-between">
                    <span className="text-slate-400">Resets:</span>
                    <span className="font-medium text-slate-200">{new Date(usage.reset_date).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div>
              <h3 className="font-medium text-white mb-3">Plan Information</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Current Tier:</span>
                  <span className="font-medium capitalize text-slate-200">{userTier}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Upgrade Available:</span>
                  <span className="font-medium text-blue-400">
                    {userTier === 'free' ? 'Professional' : userTier === 'professional' ? 'Business' : userTier === 'business' ? 'Enterprise' : 'None'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">API Endpoint:</span>
                  <span className="font-mono text-xs text-slate-300">/api/users/me/usage</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageDashboard;